import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import '../styles/landing.css';

const BASE = 'https://menu.searchisend.in';

const ContactPage = () => {
    const [contactInfo, setContactInfo] = useState({
        contact_email: 'contact@searchisend.com',
        contact_phone: '919876543210',
        contact_address: '123 Tech Park, Sector 5, Ajmer, Rajasthan, India',
        contact_hours: 'Mon - Sat: 9:00 AM - 6:00 PM',
        contact_facebook: '',
        contact_youtube: '',
        contact_instagram: ''
    });

    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        document.title = "Contact Us | SIE QR Menu";
        window.scrollTo(0, 0);
        fetchContactSettings();
    }, []);

    const fetchContactSettings = async () => {
        try {
            const res = await fetch(`${BASE}/apimenu/landing_actions.php?action=fetch_contact_settings`);
            const data = await res.json();
            if (data.success && data.settings) {
                setContactInfo(data.settings);
            }
        } catch (err) {
            console.error("Failed to load contact settings:", err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus({ type: '', message: '' });

        if (!form.name || !form.email || !form.phone || !form.message) {
            setStatus({ type: 'error', message: 'Please fill in all fields.' });
            setLoading(false);
            return;
        }

        try {
            const res = await fetch(`${BASE}/apimenu/landing_actions.php?action=submit_lead`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'submit_lead',
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    message: form.message
                })
            });
            const data = await res.json();
            if (data.success) {
                setStatus({ type: 'success', message: data.message });
                setForm({ name: '', email: '', phone: '', message: '' });
            } else {
                setStatus({ type: 'error', message: data.message || 'Something went wrong. Please try again.' });
            }
        } catch (err) {
            setStatus({ type: 'error', message: 'Failed to connect. Please check your internet connection.' });
        } finally {
            setLoading(false);
        }
    };

    const getWhatsAppUrl = (phone) => {
        const cleaned = phone.replace(/[^\d]/g, '');
        return `https://wa.me/${cleaned}`;
    };

    return (
        <div className="contact-page-wrapper" style={{ padding: '8rem 2rem 4rem 2rem', minHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="section-header" style={{ marginBottom: '4rem', textAlign: 'center' }}>
                <div className="section-tag" style={{ background: 'rgba(249, 115, 22, 0.1)', color: 'var(--lp-orange)', padding: '6px 16px', borderRadius: '50px', display: 'inline-block', fontSize: '0.85rem', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '1rem' }}>Get in Touch</div>
                <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'var(--lp-text-title, inherit)', margin: '0 0 1rem 0', letterSpacing: '-0.03em' }}>Contact Our <span>Support Team</span></h1>
                <p style={{ maxWidth: '600px', margin: '0 auto', color: 'var(--lp-text-desc, #888)', fontSize: '1.1rem', lineHeight: '1.6' }}>Have questions about plans, custom menu setups, or anything else? Reach out to us anytime.</p>
            </div>

            <div className="contact-content-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3rem', width: '100%', maxWidth: '1100px' }}>
                {/* Contact Information Cards */}
                <div className="contact-info-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div className="glass-card" style={{ padding: '2rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(10px)' }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 1.5rem 0' }}>Connect Info</h3>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                                <div style={{ background: 'rgba(249,115,22,0.1)', color: 'var(--lp-orange)', padding: '10px', borderRadius: '10px' }}>
                                    <Mail size={20} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.85rem', color: '#888', fontWeight: 500 }}>Email Address</div>
                                    <a href={`mailto:${contactInfo.contact_email}`} style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600, fontSize: '1.05rem' }}>{contactInfo.contact_email}</a>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                                <div style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', padding: '10px', borderRadius: '10px' }}>
                                    <Phone size={20} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.85rem', color: '#888', fontWeight: 500 }}>WhatsApp Us</div>
                                    <a href={getWhatsAppUrl(contactInfo.contact_phone)} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        +{contactInfo.contact_phone} <span style={{ fontSize: '0.75rem', background: '#22c55e', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>Live Chat</span>
                                    </a>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                                <div style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6', padding: '10px', borderRadius: '10px' }}>
                                    <Clock size={20} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.85rem', color: '#888', fontWeight: 500 }}>Business Hours</div>
                                    <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{contactInfo.contact_hours}</div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                                <div style={{ background: 'rgba(168,85,247,0.1)', color: '#a855f7', padding: '10px', borderRadius: '10px' }}>
                                    <MapPin size={20} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.85rem', color: '#888', fontWeight: 500 }}>Head Office Address</div>
                                    <div style={{ fontWeight: 600, fontSize: '1.05rem', lineHeight: '1.4' }}>{contactInfo.contact_address}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Social Media Links */}
                    {(contactInfo.contact_facebook || contactInfo.contact_youtube || contactInfo.contact_instagram) && (
                        <div className="glass-card" style={{ padding: '1.5rem 2rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 600, color: '#888' }}>Follow Us:</span>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                {contactInfo.contact_facebook && (
                                    <a href={contactInfo.contact_facebook} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px' }}>
                                        <i className="fab fa-facebook-f" style={{ fontSize: '1.1rem' }}></i>
                                    </a>
                                )}
                                {contactInfo.contact_youtube && (
                                    <a href={contactInfo.contact_youtube} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px' }}>
                                        <i className="fab fa-youtube" style={{ fontSize: '1.1rem' }}></i>
                                    </a>
                                )}
                                {contactInfo.contact_instagram && (
                                    <a href={contactInfo.contact_instagram} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px' }}>
                                        <i className="fab fa-instagram" style={{ fontSize: '1.1rem' }}></i>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Contact Form Card */}
                <div className="contact-form-panel">
                    <form onSubmit={handleSubmit} className="glass-card" style={{ padding: '2.5rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Send us a Message</h3>

                        {status.message && (
                            <div style={{
                                padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.95rem', fontWeight: 500,
                                background: status.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                                color: status.type === 'success' ? '#22c55e' : '#ef4444',
                                border: `1px solid ${status.type === 'success' ? '#22c55e' : '#ef4444'}`
                            }}>
                                {status.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                                {status.message}
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#888' }}>Full Name</label>
                            <input
                                type="text"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                placeholder="Your Name"
                                required
                                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'inherit', outline: 'none' }}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#888' }}>Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="you@example.com"
                                    required
                                    style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'inherit', outline: 'none' }}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#888' }}>Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="Your Phone Number"
                                    required
                                    style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'inherit', outline: 'none' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#888' }}>Message / Details</label>
                            <textarea
                                name="message"
                                value={form.message}
                                onChange={handleChange}
                                placeholder="Tell us how we can help you..."
                                required
                                rows="4"
                                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'inherit', outline: 'none', resize: 'vertical' }}
                            ></textarea>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                background: 'linear-gradient(135deg, var(--lp-orange) 0%, #ea580c 100%)',
                                color: 'white', padding: '14px', borderRadius: '8px', border: 'none',
                                fontWeight: 700, fontSize: '1.05rem', cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(249, 115, 22, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = '0 4px 15px rgba(249, 115, 22, 0.3)';
                            }}
                        >
                            {loading ? (
                                <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.1rem' }}></i>
                            ) : (
                                <>
                                    <Send size={18} /> Send Message
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
